<template>
  <DialogComponent
    ref="modal"
    dialogId="package-payment-modal"
    type="bottomsheet"
    noInnerPadding
    @closed="resetState"
  >
    <div class="package-payment-modal">
      <!-- Header with gradient -->
      <div
        class="relative bg-gradient-to-br from-orange-500 to-primary-600 rounded-t-3xl px-6 pt-8 pb-10"
      >
        <div class="text-center">
          <div
            class="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl mb-4 shadow-lg"
          >
            <svg
              class="w-10 h-10 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2.5"
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              />
            </svg>
          </div>
          <h2 class="text-2xl font-bold text-white">
            Paiement de la livraison
          </h2>
          <p class="text-orange-100 text-sm mt-1">
            Choisissez votre mode de paiement
          </p>
        </div>
      </div>

      <!-- Content Card -->
      <div class="relative -mt-6 px-4 pb-4">
        <div
          class="bg-white rounded-3xl shadow-xl shadow-slate-200/50 p-6 space-y-6"
        >
          <!-- Amount Summary -->
          <div
            class="bg-gradient-to-r from-slate-50 to-slate-100 border-2 border-slate-200 rounded-2xl p-4"
          >
            <div class="flex items-center justify-between">
              <div>
                <p class="text-xs text-slate-500 font-medium">
                  Montant à payer
                </p>
                <p class="text-2xl font-bold text-slate-800">
                  {{ formatAmount(totalAmount) }} XOF
                </p>
              </div>
              <div
                class="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center"
              >
                <svg
                  class="w-6 h-6 text-slate-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <div class="mt-2 pt-2 border-t border-slate-200">
              <p class="text-xs text-slate-500 flex justify-between">
                <span>Livraison</span>
                <span class="font-semibold text-slate-700"
                  >{{ formatAmount(deliveryCost) }} XOF</span
                >
              </p>
              <p
                class="text-xs text-slate-500 flex justify-between mt-1"
                v-if="paymentFee > 0"
              >
                <span>Frais de paiement</span>
                <span class="font-semibold text-slate-700"
                  >{{ formatAmount(paymentFee) }} XOF</span
                >
              </p>
            </div>
          </div>

          <!-- Payment Method Selection -->
          <div class="space-y-3">
            <label
              class="block text-sm font-semibold text-slate-700 flex items-center gap-2"
            >
              <svg
                class="w-4 h-4 text-orange-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
              Mode de paiement
            </label>
            <div class="grid grid-cols-1 gap-3">
              <!-- Wallet Option (hidden when mobileMoneyOnly) -->
              <button
                v-if="!escrowEnabled && !mobileMoneyOnly"
                type="button"
                class="p-4 rounded-2xl border-2 transition-all duration-300 relative overflow-hidden group"
                :class="
                  form.paymentMethod === 'wallet'
                    ? 'border-emerald-500 bg-emerald-50 shadow-lg shadow-emerald-500/20 scale-105'
                    : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
                "
                @click="selectPaymentMethod('wallet')"
              >
                <div
                  class="absolute inset-0 bg-gradient-to-br from-transparent to-black/5 opacity-0 group-hover:opacity-100 transition-opacity"
                ></div>
                <div class="relative flex items-center gap-4">
                  <div
                    class="w-14 h-14 rounded-2xl flex items-center justify-center shadow-md bg-gradient-to-br from-emerald-400 to-emerald-600 text-white"
                  >
                    <svg
                      class="w-7 h-7"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                      />
                    </svg>
                  </div>
                  <div class="flex-1 text-left">
                    <p
                      class="text-base font-bold"
                      :class="
                        form.paymentMethod === 'wallet'
                          ? 'text-emerald-700'
                          : 'text-slate-800'
                      "
                    >
                      Portefeuille
                    </p>
                    <p class="text-xs text-slate-500">
                      Solde: {{ formatAmount(walletBalance) }} XOF
                    </p>
                  </div>
                  <div
                    v-if="form.paymentMethod === 'wallet'"
                    class="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center shadow-md"
                  >
                    <svg
                      class="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="3"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>
              </button>

              <!-- Mobile Money Option -->
              <button
                type="button"
                class="p-4 rounded-2xl border-2 transition-all duration-300 relative overflow-hidden group"
                :class="
                  form.paymentMethod === 'mobile_money'
                    ? 'border-orange-500 bg-orange-50 shadow-lg shadow-orange-500/20 scale-105'
                    : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
                "
                @click="selectPaymentMethod('mobile_money')"
              >
                <div
                  class="absolute inset-0 bg-gradient-to-br from-transparent to-black/5 opacity-0 group-hover:opacity-100 transition-opacity"
                ></div>
                <div class="relative flex items-center gap-4">
                  <div
                    class="w-14 h-14 rounded-2xl flex items-center justify-center shadow-md bg-gradient-to-br from-orange-400 to-orange-600 text-white"
                  >
                    <svg
                      class="w-7 h-7"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div class="flex-1 text-left">
                    <p
                      class="text-base font-bold"
                      :class="
                        form.paymentMethod === 'mobile_money'
                          ? 'text-orange-700'
                          : 'text-slate-800'
                      "
                    >
                      Mobile Money
                    </p>
                    <p class="text-xs text-slate-500">MTN, Moov, Celtiis</p>
                  </div>
                  <div
                    v-if="form.paymentMethod === 'mobile_money'"
                    class="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center shadow-md"
                  >
                    <svg
                      class="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="3"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>
              </button>

              <!-- Cash Option (hidden when mobileMoneyOnly) (Désactivé) -->
              <!--
              <button
                v-if="!mobileMoneyOnly"
                type="button"
                class="p-4 rounded-2xl border-2 transition-all duration-300 relative overflow-hidden group"
                :class="
                  form.paymentMethod === 'cash'
                    ? 'border-primary-500 bg-primary-50 shadow-lg shadow-primary-500/20 scale-105'
                    : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
                "
                @click="selectPaymentMethod('cash')"
              >
                <div
                  class="absolute inset-0 bg-gradient-to-br from-transparent to-black/5 opacity-0 group-hover:opacity-100 transition-opacity"
                ></div>
                <div class="relative flex items-center gap-4">
                  <div
                    class="w-14 h-14 rounded-2xl flex items-center justify-center shadow-md bg-gradient-to-br from-primary-400 to-primary-600 text-white"
                  >
                    <svg
                      class="w-7 h-7"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                  </div>
                  <div class="flex-1 text-left">
                    <p
                      class="text-base font-bold"
                      :class="
                        form.paymentMethod === 'cash'
                          ? 'text-primary-700'
                          : 'text-slate-800'
                      "
                    >
                      Espèces
                    </p>
                    <p class="text-xs text-slate-500">Paiement au livreur</p>
                  </div>
                  <div
                    v-if="form.paymentMethod === 'cash'"
                    class="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center shadow-md"
                  >
                    <svg
                      class="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="3"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>
              </button>
              -->
            </div>
            <div
              v-if="errors.paymentMethod"
              class="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-xl"
            >
              <svg
                class="w-4 h-4 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {{ errors.paymentMethod }}
            </div>
          </div>

          <!-- Wallet: Insufficient Balance Warning -->
          <div
            v-if="
              form.paymentMethod === 'wallet' && walletBalance < totalAmount
            "
            class="bg-rose-50 border-2 border-rose-200 rounded-2xl p-4"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center flex-shrink-0"
              >
                <svg
                  class="w-5 h-5 text-rose-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div class="flex-1">
                <p class="text-sm font-semibold text-rose-800">
                  Solde insuffisant
                </p>
                <p class="text-xs text-rose-600 mt-0.5">
                  Il vous manque
                  {{ formatAmount(totalAmount - walletBalance) }} XOF
                </p>
              </div>
            </div>
            <button
              type="button"
              class="w-full mt-3 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2"
              @click="goToDeposit"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2.5"
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Effectuer un dépôt
            </button>
          </div>

          <!-- Mobile Money: Provider & Phone -->
          <div v-if="form.paymentMethod === 'mobile_money'" class="space-y-4">
            <!-- Provider Selection -->
            <div class="space-y-3" v-if="false">
              <label class="block text-sm font-semibold text-slate-700"
                >Opérateur mobile</label
              >
              <div class="grid grid-cols-2 gap-3">
                <button
                  v-for="provider in providers"
                  :key="provider.id"
                  type="button"
                  class="p-3 rounded-2xl border-2 transition-all duration-300 relative overflow-hidden group"
                  :class="
                    form.provider === provider.id
                      ? 'border-orange-500 bg-orange-50 shadow-lg shadow-orange-500/20 scale-105'
                      : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
                  "
                  @click="selectProvider(provider.id)"
                >
                  <div class="relative flex flex-col items-center gap-2">
                    <div
                      class="w-12 h-12 rounded-xl flex items-center justify-center shadow-md"
                      :class="provider.bgClass"
                    >
                      <svg
                        v-if="provider.id === 'mtn'"
                        class="w-6 h-6"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path
                          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8-8zm2-9h-4V7h4v4z"
                        />
                      </svg>
                      <svg
                        v-else
                        class="w-6 h-6"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <circle cx="12" cy="12" r="10" fill="currentColor" />
                        <path
                          d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4-4z"
                          fill="#FFC72C"
                        />
                        <path
                          d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4z"
                          fill="#fff"
                        />
                      </svg>
                    </div>
                    <span
                      class="text-xs font-semibold"
                      :class="
                        form.provider === provider.id
                          ? 'text-orange-700'
                          : 'text-slate-700'
                      "
                      >{{ provider.name }}</span
                    >
                  </div>
                  <div
                    v-if="form.provider === provider.id"
                    class="absolute top-1 right-1 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center shadow-md"
                  >
                    <svg
                      class="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="3"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </button>
              </div>
            </div>

            <!-- Phone Number -->
            <div v-if="!escrowEnabled" class="space-y-3">
              <label class="block text-sm font-semibold text-slate-700"
                >Numéro de téléphone</label
              >
              <div class="relative">
                <div
                  class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"
                >
                  <svg
                    class="w-5 h-5 text-slate-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                </div>
                <input
                  v-model="form.phoneNumber"
                  type="tel"
                  name="phoneNumber"
                  class="w-full pl-12 pr-4 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-800 font-medium focus:border-orange-500 focus:bg-white focus:ring-4 focus:ring-orange-500/10 transition-all outline-none"
                  :class="{
                    'border-red-300 bg-red-50': errors.phoneNumber,
                    'border-orange-500 bg-orange-50':
                      form.phoneNumber && !errors.phoneNumber
                  }"
                  placeholder="01 XX XX XX XX"
                  @input="validatePhoneNumber"
                />
              </div>
              <div
                v-if="errors.phoneNumber"
                class="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-xl"
              >
                <svg
                  class="w-4 h-4 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {{ errors.phoneNumber }}
              </div>
            </div>
          </div>

          <!-- Processing Indicator -->
          <div
            v-if="isProcessing"
            class="bg-primary-50 border-2 border-primary-200 rounded-2xl p-4"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center"
              >
                <svg
                  class="w-5 h-5 text-primary-600 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
              <div class="flex-1">
                <p class="text-sm font-semibold text-primary-800">
                  {{ processingMessage }}
                </p>
                <p
                  class="text-xs text-primary-600 mt-0.5"
                  v-if="processingSubMessage"
                >
                  {{ processingSubMessage }}
                </p>
              </div>
            </div>
          </div>

          <!-- Confirm Button -->
          <button
            type="button"
            class="w-full py-4 rounded-2xl font-bold text-lg transition-all duration-300 active:scale-95 flex items-center justify-center gap-3"
            :class="
              isFormValid && !isProcessing
                ? 'bg-gradient-to-r from-orange-500 to-primary-600 text-white shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 hover:scale-[1.02]'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            "
            :disabled="!isFormValid || isProcessing"
            @click="handleConfirm"
          >
            <template v-if="!isProcessing">
              <svg
                class="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2.5"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              {{ confirmButtonText }}
            </template>
            <template v-else>
              <svg class="animate-spin w-6 h-6" fill="none" viewBox="0 0 24 24">
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Traitement en cours...
            </template>
          </button>

          <!-- Cancel Button -->
          <button
            v-if="!isProcessing"
            type="button"
            class="w-full py-3.5 rounded-xl font-semibold text-slate-600 hover:bg-slate-100 transition-all duration-300 active:scale-95"
            @click="hideModal"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  </DialogComponent>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';

const props = defineProps({
  totalAmount: {
    type: Number,
    required: true
  },
  deliveryCost: {
    type: Number,
    default: 0
  },
  paymentFee: {
    type: Number,
    default: 0
  },
  escrowEnabled: {
    type: Boolean,
    default: false
  },
  // Force Mobile Money as the only payment method (used on the public payment page)
  mobileMoneyOnly: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['paymentConfirmed', 'cancelled']);

const modal = ref(null);
const form = ref({
  paymentMethod: null,
  provider: null,
  phoneNumber: ''
});
const errors = ref({
  paymentMethod: null,
  provider: null,
  phoneNumber: null
});
const isProcessing = ref(false);
const processingMessage = ref('');
const processingSubMessage = ref('');
const walletBalance = ref(0);

const providers = [
  {
    id: 'moov',
    name: 'Moov',
    bgClass: 'bg-gradient-to-br from-yellow-400 to-yellow-500 text-white'
  },
  {
    id: 'mtn',
    name: 'MTN',
    bgClass: 'bg-gradient-to-br from-yellow-300 to-yellow-400 text-black'
  }
];

function showModal() {
  // Auto-select Mobile Money when it's the only option
  if (props.mobileMoneyOnly) {
    form.value.paymentMethod = 'mobile_money';
  }
  // Load wallet balance when opening
  bus.emit(constants.EVENTS.WALLET.BALANCE_LOAD);
  modal.value?.show();
}

function hideModal() {
  if (!isProcessing.value) {
    modal.value?.hide();
    emit('cancelled');
  }
}

function formatAmount(amount) {
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

function selectPaymentMethod(method) {
  form.value.paymentMethod = method;
  errors.value.paymentMethod = null;
  // Reset mobile money fields when switching
  if (method !== 'mobile_money') {
    form.value.provider = null;
    form.value.phoneNumber = '';
  }
}

function selectProvider(providerId) {
  form.value.provider = providerId;
  errors.value.provider = null;
}

function stopProcessing() {
  isProcessing.value = false;
  processingMessage.value = '';
  processingSubMessage.value = '';
}

function validatePhoneNumber() {
  if (props.escrowEnabled) {
    errors.value.phoneNumber = null;
    return true;
  }
  const phoneRegex = /^[0-9]{8,12}$/;
  const phone = form.value.phoneNumber.replace(/\s/g, '');
  if (!phone) {
    errors.value.phoneNumber = 'Veuillez entrer votre numéro';
    return false;
  }
  if (!phoneRegex.test(phone)) {
    errors.value.phoneNumber = 'Numéro de téléphone invalide';
    return false;
  }
  errors.value.phoneNumber = null;
  return true;
}

const isFormValid = computed(() => {
  if (!form.value.paymentMethod) return false;

  if (form.value.paymentMethod === 'wallet') {
    return walletBalance.value >= props.totalAmount;
  }

  if (form.value.paymentMethod === 'mobile_money') {
    if (props.escrowEnabled) return true;
    return (
      // form.value.provider &&
      form.value.phoneNumber &&
      form.value.phoneNumber.length >= 8 &&
      !errors.value.phoneNumber
    );
  }

  if (form.value.paymentMethod === 'cash') {
    return true;
  }

  return false;
});

const confirmButtonText = computed(() => {
  switch (form.value.paymentMethod) {
    case 'wallet':
      return `Payer ${formatAmount(props.totalAmount)} XOF`;
    case 'mobile_money':
      return 'Payer avec Mobile Money';
    case 'cash':
      return 'Confirmer (Paiement au livreur)';
    default:
      return 'Continuer';
  }
});

function handleConfirm() {
  if (!isFormValid.value) return;

  isProcessing.value = true;
  processingMessage.value = 'Création de la livraison...';
  processingSubMessage.value = 'Envoi de votre demande en cours';

  if (form.value.paymentMethod === 'wallet') {
    if (walletBalance.value < props.totalAmount) {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        type: 'error',
        title: 'Solde insuffisant',
        body: 'Votre solde est insuffisant pour ce paiement'
      });
      isProcessing.value = false;
      return;
    }
    // Émettre directement — le serveur gère le débit du wallet
    emit('paymentConfirmed', {
      paymentMethod: 'wallet',
      provider: 'wallet',
      phoneNumber: '',
      walletId: 'user-wallet'
    });
  } else if (form.value.paymentMethod === 'mobile_money') {
    // Émettre directement — le serveur déclenche le paiement Mobile Money
    let phone = '';
    if (!props.escrowEnabled) {
      phone = form.value.phoneNumber.replace(/[^\d]/g, '');
      phone = phone.startsWith('01') ? phone : '01' + phone;
      phone = phone.startsWith('229') ? phone : '229' + phone;
      console.log('🚀 ~ handleConfirm ~ phone:', phone);
      if (phone.length !== 13) {
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          type: 'error',
          title: 'Numéro de téléphone invalide',
          body: 'Veuillez entrer un numéro de téléphone valide'
        });
        isProcessing.value = false;
        return;
      }
    }
    emit('paymentConfirmed', {
      paymentMethod: 'mobile_money',
      provider: 'fedapay',
      phoneNumber: phone,
      walletId: ''
    });
  } else if (form.value.paymentMethod === 'cash') {
    // Émettre directement — paiement en espèces au livreur
    emit('paymentConfirmed', {
      paymentMethod: 'cash',
      provider: 'cash',
      phoneNumber: '',
      walletId: ''
    });
  }
}

function goToDeposit() {
  hideModal();
  bus.emit(constants.EVENTS.WALLET.DEPOSIT_OPEN);
}

function onBalanceUpdate(data) {
  walletBalance.value = data?.balance || 0;
}

function resetState() {
  form.value = { paymentMethod: null, provider: null, phoneNumber: '' };
  errors.value = { paymentMethod: null, provider: null, phoneNumber: null };
  isProcessing.value = false;
  processingMessage.value = '';
  processingSubMessage.value = '';
}

onMounted(() => {
  // Charge le solde du wallet si nécessaire pour l'option wallet
  bus.on(constants.EVENTS.WALLET.BALANCE_UPDATE, onBalanceUpdate);
});

onBeforeUnmount(() => {
  bus.off(constants.EVENTS.WALLET.BALANCE_UPDATE, onBalanceUpdate);
});

defineExpose({
  showModal,
  hideModal,
  stopProcessing // <-- Exposer cette méthode
});
</script>
